import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  Type,
  AlignLeft,
  List,
  CheckSquare,
  Calendar,
  Clock,
  Mail,
  Phone,
  DollarSign,
  Gauge,
  Grid3x3,
} from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/shared/components/ui/command';
import type { FormFieldType } from '../../types';

const FIELD_ICONS: Record<FormFieldType, React.ComponentType<{ className?: string }>> = {
  Text: Type,
  TextArea: AlignLeft,
  Select: List,
  MultiSelect: CheckSquare,
  Checkbox: CheckSquare,
  Date: Calendar,
  Time: Clock,
  Email: Mail,
  Phone: Phone,
  Number: DollarSign,
  Currency: DollarSign,
  LinearScale: Gauge,
  Grid: Grid3x3,
};

const FIELD_GROUPS: { groupKey: string; types: FormFieldType[] }[] = [
  { groupKey: 'groupText', types: ['Text', 'TextArea'] },
  { groupKey: 'groupSelection', types: ['Select', 'MultiSelect'] },
  { groupKey: 'groupDate', types: ['Date', 'Time'] },
  { groupKey: 'groupContact', types: ['Email', 'Phone'] },
  { groupKey: 'groupRating', types: ['LinearScale', 'Grid'] },
  { groupKey: 'groupOther', types: ['Currency'] },
];

interface AddFieldMenuProps {
  onSelect: (fieldType: FormFieldType) => void;
}

export function AddFieldMenu({ onSelect }: AddFieldMenuProps) {
  const { t } = useTranslation('forms');
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="w-full justify-start"
          data-testid="forms-builder-add-field-button"
        >
          <Plus className="h-4 w-4 mr-2" />
          {t('builder.addFieldButton')}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <Command>
          <CommandInput placeholder={t('builder.addFieldSearchPlaceholder')} />
          <CommandList>
            {FIELD_GROUPS.map((group) => (
              <CommandGroup
                key={group.groupKey}
                heading={t(`builder.fieldTypes.${group.groupKey}`)}
              >
                {group.types.map((type) => {
                  const Icon = FIELD_ICONS[type];
                  return (
                    <CommandItem
                      key={type}
                      value={t(`builder.fieldTypes.${type}`)}
                      onSelect={() => {
                        onSelect(type);
                        setOpen(false);
                      }}
                      data-testid={`forms-builder-add-field-option-${type}`}
                    >
                      <Icon className="h-4 w-4 mr-2" />
                      {t(`builder.fieldTypes.${type}`)}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
